import { motion } from 'framer-motion';
import { Check, Clock, Shield, TrendingUp, Zap, Heart } from 'lucide-react';

const features = [
  {
    icon: Clock,
    title: 'Employee Convenience',
    description:
      'Simplified ordering process saves time and enhances satisfaction with pre-orders, live menu displays, and quick pickups.',
  },
  {
    icon: Zap,
    title: 'Operational Efficiency',
    description:
      'Reduced queues, automated billing, clear reporting, and streamlined cafeteria operations for maximum productivity.',
  },
  {
    icon: Shield,
    title: 'Health & Hygiene',
    description:
      'Contactless ordering and payment minimize physical contact, ensuring a safer dining environment for all.',
  },
  {
    icon: TrendingUp,
    title: 'Scalability',
    description:
      'Designed to support offices of all sizes — from startups to enterprise campuses with thousands of employees.',
  },
  {
    icon: Heart,
    title: 'Enhanced Experience',
    description:
      'Pre-orders, live menu displays, and feedback loops create an engaging and delightful dining experience.',
  },
  {
    icon: Check,
    title: 'Budget Control',
    description:
      'Analytics-driven insights for better cost management, reduced food waste, and optimized meal planning.',
  },
];

const differentiators = [
  'All-in-one cafeteria solution (mobile app, QR, kiosk, meal card, POS)',
  'Flexible & customizable deployment options',
  'Secure & reliable platform with multiple payment options',
  'Analytics-driven insights for budget control',
  'Enhanced dining experience with feedback loops',
  'Compliance and transparency focused',
];

const WhyChooseUs = () => {
  return (
    <section
      id="why-us"
      className="section-padding bg-white"
      data-testid="why-choose-us-section"
    >
      <div className="container-custom">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="section-header"
        >
          <span className="text-[#0066CC] text-sm font-semibold uppercase tracking-wider mb-4 block">
            Why Choose Us
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            The Smart Choice for Digital Cafeteria
          </h2>
          <div className="accent-bar mx-auto mb-6" />
          <p className="text-base md:text-lg text-gray-600 leading-relaxed">
            Partnering with clients to transform the corporate cafeteria into a
            modern, tech-enabled dining experience that's smarter, faster, and
            built for the future of work.
          </p>
        </motion.div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="flex gap-4 p-6 rounded-2xl bg-gray-50 border border-gray-100 card-hover group"
              data-testid={`feature-card-${index}`}
            >
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-[#0066CC]/10 rounded-xl flex items-center justify-center group-hover:bg-[#0066CC] transition-colors duration-300">
                  <feature.icon className="w-6 h-6 text-[#0066CC] group-hover:text-white transition-colors duration-300" />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Differentiators */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          // className="bg-gradient-to-br from-[#0066CC] to-[#0052A3] rounded-3xl p-8 md:p-12 text-white"
          className="bg-gradient-to-br from-[#FDDC5C] to-[#FDDC5C] rounded-3xl p-8 md:p-12 text-blue"
          data-testid="differentiators-section"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-2xl md:text-3xl font-bold mb-6">
                What Sets Us Apart
              </h3>
              <p className="text-blue/80 leading-relaxed mb-8">
                Our platform offers unique advantages that make us the preferred
                choice for organizations seeking comprehensive cafeteria
                digitization.
              </p>
              <div className="space-y-4">
                {differentiators.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    className="flex items-center gap-3"
                    data-testid={`differentiator-${index}`}
                  >
                    <div className="check-icon bg-white">
                      <Check className="w-4 h-4 text-[#fff]" />
                    </div>
                    <span className="text-blue/90">{item}</span>
                  </motion.div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 md:p-8 border border-white/20">
                <div className="text-center mb-8">
                  <div className="text-5xl md:text-6xl font-bold text-blue mb-2">
                    99%
                  </div>
                  <div className="text-blue/80">Customer Satisfaction Rate</div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-white/10 rounded-xl">
                    <div className="text-2xl font-bold text-blue">24/7</div>
                    <div className="text-sm text-blue/70">Support</div>
                  </div>
                  <div className="text-center p-4 bg-white/10 rounded-xl">
                    <div className="text-2xl font-bold text-blue">20+</div>
                    <div className="text-sm text-blue/70">Integrations</div>
                  </div>
                  <div className="text-center p-4 bg-white/10 rounded-xl">
                    <div className="text-2xl font-bold text-blue">99.9%</div>
                    <div className="text-sm text-blue/70">Uptime SLA</div>
                  </div>
                  <div className="text-center p-4 bg-white/10 rounded-xl">
                    <div className="text-2xl font-bold text-blue">5 Min</div>
                    <div className="text-sm text-blue/70">Avg Response</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
